import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

interface Props {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  children: ReactNode;
  sentinelText?: string;
}

// 无限滚动：滚动到底部时自动加载下一页。
// 采用边沿触发：仅当哨兵从不可见变为可见时触发一次加载；
// 列表很短（哨兵始终可见）时只在首次进入时加载一页，避免反复自动加载。
export function InfiniteScroll({ hasMore, loading, onLoadMore, children, sentinelText = "加载中..." }: Props) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  // 记录哨兵上一次是否可见（跨观察器生命周期保留，防止重建观察器后重复触发）
  const wasVisible = useRef(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries[0]?.isIntersecting ?? false;
        // 边沿触发：只有「进入」可视区（false -> true）且可加载时才触发
        if (isVisible && !wasVisible.current && hasMore && !loading) {
          onLoadMore();
        }
        wasVisible.current = isVisible;
      },
      { rootMargin: "80px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, onLoadMore]);

  return (
    <div>
      {children}
      <div ref={sentinelRef} className="flex justify-center py-4 flat text-13px">
        {loading ? sentinelText : hasMore ? "" : "没有更多了"}
      </div>
    </div>
  );
}
